import React from "react";
import MenuItem from '@mui/material/MenuItem';
import { TextField } from "@mui/material";
import { PutProductDistributorOfferingStatus, PutProductDistributorFulfillmentType } from "../../ProductSettings/hooks/useFetchProductSettingsDistributorOverrides";
import { useSKUOfferingStatusesContext } from "../../../Contexts/skuOfferingStatusesContext";
import { useDistributorSettingsContext } from "../context/DistributorSettingsContext";
import PropTypes from 'prop-types';
import { SkuOfferingStatus } from "../../../Enums/SkuOfferingStatus";

const isFulfillmentTypeValid = (fulfillmentType, possibleFulfillmentTypes) =>
    possibleFulfillmentTypes.skuFulfillmentTypes.some(type => type.id === fulfillmentType?.id);

const resolveFulfillmentType = (currentFulfillmentType, possibleFulfillmentTypes, customerType, offeringStatus, fulfillmentMethodMappings) => {
    if (isFulfillmentTypeValid(currentFulfillmentType, possibleFulfillmentTypes)) {
        return currentFulfillmentType;
    }

    const defaultMapping = fulfillmentMethodMappings.find(mapping =>
        mapping.customerType === customerType &&
        mapping.offeringStatus === offeringStatus &&
        mapping.isDefault
    );

    if (defaultMapping) {
        return { id: defaultMapping.fulfillmentType, description: defaultMapping.fulfillmentTypeDescription };
    }

    // Fallback to first available option if no default found
    return possibleFulfillmentTypes.skuFulfillmentTypes[0];
};

export const SkuOfferingStatusSelect = ({
    row,
    originalRow,
    handleUpdateRow,
}) => {
    const { skuOfferingStatuses } = useSKUOfferingStatusesContext();
    const { customerType, getPossibleSkuFulfillmentTypes, fulfillmentMethodMappings } = useDistributorSettingsContext();

    const updateProductDistributorAssortment = async (skuRow, originalRow) => {
        const offeringStatusChanged = skuRow.skuOfferingStatus?.id !== originalRow?.skuOfferingStatus?.id;
        const fulfillmentTypeChanged = skuRow.skuFulfillmentType?.id !== originalRow?.skuFulfillmentType?.id;
        // The PUT helpers read `.id` as the distributor id; our rows key `.id` by sku, so pass a
        // proxy object with `.id` overridden to this row's fixed distributor id.
        const putTarget = { ...skuRow, id: skuRow.distributorId };

        if (offeringStatusChanged) {
            await PutProductDistributorOfferingStatus(skuRow.product, putTarget);
        }

        if (fulfillmentTypeChanged || (skuRow.skuFulfillmentType && skuRow.skuFulfillmentType.id)) {
            await PutProductDistributorFulfillmentType(skuRow.product, putTarget);
        }
    };

    return (<TextField
        select
        value={row.skuOfferingStatus?.id ?? SkuOfferingStatus.NOT_OFFERED}
        size="small"
        fullWidth
        onChange={async (event) => {
            const newOfferingStatus = event.target.value;
            const possibleFulfillmentTypes = getPossibleSkuFulfillmentTypes(customerType, newOfferingStatus);
            const newFulfillmentType = resolveFulfillmentType(row.skuFulfillmentType, possibleFulfillmentTypes, customerType, newOfferingStatus, fulfillmentMethodMappings);

            const tempRow = {
                ...row,
                skuOfferingStatus: skuOfferingStatuses.find(status => status.id === newOfferingStatus),
                skuFulfillmentType: newFulfillmentType
            };

            await updateProductDistributorAssortment(tempRow, originalRow);
            await handleUpdateRow(tempRow);
        }}
    >
        {skuOfferingStatuses?.map(status => <MenuItem key={status.id}
            value={status.id}>
            {status.description}
        </MenuItem>)}
    </TextField>

    );
}

SkuOfferingStatusSelect.propTypes = {
    handleUpdateRow: PropTypes.func,
    row: PropTypes.object,
    originalRow: PropTypes.object
}
